/**
 * @fileoverview FieldRenderer — single dispatcher for all 18 canonical widgets.
 * @module inspection-renderer/fields
 *
 * Responsibilities:
 * - Map `field.type` (canonical FieldType) to the correct widget component.
 * - Forward the resolved engine-v2 state (`hidden / disabled / required`)
 *   into the widget via `BaseWidgetProps.resolved`.
 * - Funnel widget value changes through a single `onFieldChange(fieldId, next)`
 *   callback so the host adapter (Phase 4) sees a uniform stream.
 * - Honor B16 — `visibility: 'always'` short-circuits any `hidden` rule.
 * - Honor G-025 — section / instructions / repeatableList / dataBound /
 *   inspectors are
 *   first-class branches in this switch (mobile-pwa lacked them).
 *
 * Dependencies:
 * - React 18 (peer).
 * - All 18 widget components in the same folder.
 * - `@firesquirrel/core-domain` canonical types.
 *
 * Architecture:
 * - Presentation-only. Holds no state; pure dispatch.
 * - The `renderNested` prop passed to `<RepeatableListField>` is this same
 *   component, enabling recursive nested-template rendering.
 *
 * Notes:
 * - The dispatcher knowingly accepts `unknown` for `value`; the per-widget
 *   component is responsible for narrowing safely. This keeps the public
 *   adapter contract simple.
 *
 * @component FieldRenderer
 *
 * Purpose: dispatch a single canonical field to its widget.
 *
 * Props:
 * - field: canonical InspectionFormField.
 * - value: current persisted value (any shape — widget narrows).
 * - isNA: when true, the N/A pill is active.
 * - resolved: engine-v2 resolved state for this field.
 * - onFieldChange: `(fieldId, next) => void` — bubbled up by the widget.
 * - onToggleNA: `(fieldId, next) => void` — bubbled up by the wrapper.
 * - bulkPilotMode: when true, all controls are disabled (B14).
 * - requiredImmediate: show missing-required feedback on first conduct render.
 * - errorMessage: per-field validation error to surface.
 *
 * Behavior:
 * - Returns `null` when `resolved.hidden === true` AND `visibility !== 'always'`.
 * - Unknown field types fall through to a small dev-only `<UnknownField />`
 *   placeholder so tests can detect dispatch-table drift (G-025 carrier).
 *
 * Data Source:
 * - local (Dexie-backed value passed in by the host; pure pass-through here).
 *
 * Constraints:
 * - No API calls, no Dexie writes, no host imports.
 *
 * @contract FieldRendererProps
 * Purpose: input contract for the per-field dispatcher; uniform across the
 * 18 supported widget types.
 */
import { useEffect, useState, type ReactNode } from 'react';
import type {
  InspectionFormField,
  ResolvedFieldStateV2,
} from '@firesquirrel/core-domain';
import { isFieldAnswered, isRequiredEffective } from '../validation/fieldIssues';
import { resolveFieldValueForValidation } from '../validation/dataBoundFieldValue';
import { useResolveContext } from '../contracts/rendererContext';
import { resolveFieldDefault } from './fieldDefaults';
import { FieldViolationCaptures } from '../violations/FieldViolationCaptures';
import { useViolationsCapture } from '../violations/violationsCaptureContext';
import { isPassFailField, isPassFailFailing } from '../violations/passFailState';
import { ToggleField } from './ToggleField';
import { TextField } from './TextField';
import { TextareaField } from './TextareaField';
import { NumberField } from './NumberField';
import { MeasurementField } from './MeasurementField';
import { DropdownField } from './DropdownField';
import { CheckboxGroupField } from './CheckboxGroupField';
import { RadioGroupField } from './RadioGroupField';
import { CameraField } from './CameraField';
import { DateField } from './DateField';
import { DateTimeField } from './DateTimeField';
import { PhotoField } from './PhotoField';
import { SignatureField } from './SignatureField';
import { LocationField } from './LocationField';
import { SectionHeadingField } from './SectionHeadingField';
import { InstructionsField } from './InstructionsField';
import { RepeatableListField } from './RepeatableListField';
import { DataBoundField } from './DataBoundField';
import { InspectorField } from './InspectorField';

/**
 * Field types that manage their own default-value application or represent
 * non-data-capturing widgets. FieldRenderer's mount-time default-apply
 * effect skips these to avoid double-commits or meaningless defaults.
 *
 * - date/dateTime: their own useEffect resolves all defaults including 'today'/'now'.
 * - section/instructions: no captured data.
 * - dataBound: value comes from an external resolver, not field.defaultValue.
 * - repeatableList/camera/photo/signature/location/inspectors: complex or
 *   media-capture types with no simple programmatic default.
 */
const NON_DEFAULT_FIELD_TYPES = new Set([
  'section', 'instructions', 'dataBound',
  'repeatableList', 'camera', 'photo',
  'signature', 'location', 'inspectors',
  'date', 'dateTime',
]);

/**
 * @contract FieldRendererProps
 * Purpose: Describes field renderer props at the shared type boundary.
 * Fields: field, value, isNA, resolved, formId, onFieldChange, onToggleNA, bulkPilotMode, readOnly, requiredImmediate, dataBoundReadOnly, errorMessage, resetKey
 * Rules: Keep the declared field types and optionality consistent at call sites.
 * Sync: Persistence and synchronization remain owned by the consuming service.
 */
export interface FieldRendererProps {
  field: InspectionFormField;
  value: unknown;
  isNA?: boolean;
  resolved?: ResolvedFieldStateV2;
  /** Form id — required for inline violation capture persistence. */
  formId?: string;
  onFieldChange: (fieldId: string, next: unknown) => void;
  onToggleNA?: (fieldId: string, next: boolean) => void;
  bulkPilotMode?: boolean;
  /** When true, the inspection is completed or under review; all controls are disabled. */
  readOnly?: boolean;
  requiredImmediate?: boolean;
  /** Lock only data-bound widgets when ordinary answers remain editable. */
  dataBoundReadOnly?: boolean;
  errorMessage?: string;
  /**
   * Opaque key that changes whenever the host resets session state (e.g. preview
   * canvas Reset button). Changing this key causes the mount-time default-value
   * effect to re-fire, re-applying `field.defaultValue` as if the form were
   * loaded fresh. Hosts that never reset (conduct mode) may omit this.
   */
  resetKey?: string | number;
}

/** Dev-only fallback when `field.type` is unknown. Tests assert this exists. */
function UnknownField({ field }: { field: InspectionFormField }): ReactNode {
  return (
    <div
      data-field-id={field.id}
      data-widget-type="unknown"
      role="alert"
      className="text-xs text-red-600 border border-red-200 bg-red-50 rounded-sm px-2 py-1"
    >
      Unknown widget type: <code>{String(field.type)}</code>
    </div>
  );
}

/**
 * @component FieldRenderer
 * Purpose: Renders field renderer for the current workflow.
 * Props: Inputs: props; detailed shape follows the declared props.
 * Behavior: Updates presentation from supplied inputs and existing local hooks.
 * Data Source: Typed props and existing local service or hook state.
 * Constraints: Persistence stays behind the service or adapter boundary.
 * Performance: Keep subscriptions and rendering scoped to current inputs.
 */
export function FieldRenderer(props: FieldRendererProps): ReactNode {
  const { field, value, isNA, formId, onFieldChange, onToggleNA, bulkPilotMode, readOnly, errorMessage, resetKey } = props;
  const resolved: ResolvedFieldStateV2 | undefined = readOnly || bulkPilotMode
    ? (props.resolved ? { ...props.resolved, disabled: true } : undefined)
    : props.resolved;
  const resolveContext = useResolveContext();

  // Guard rails: once a violation with at least one occurrence has been
  // logged on a fail-capable widget, the inspector CANNOT flip the field
  // back to a passing value without first removing every occurrence.
  // Rationale (per legal/data-integrity requirements): a violation that
  // was witnessed by an inspector is a historical fact that cannot be
  // un-observed; the only recourse is Repair or Remove, never “flip back
  // to pass.” The modal is single-button OK — the inspector must remove
  // occurrences via the per-occurrence Remove button to proceed.
  const violationsCtx = useViolationsCapture();
  const [blockedChange, setBlockedChange] = useState<{ occurrenceCount: number; kind: 'passFlip' | 'naToggle' } | null>(null);

  // B16 — `visibility: 'always'` short-circuits hide rules.
  const effectiveHidden = resolved?.hidden && field.visibility !== 'always';
  const effectiveDisabled = !!resolved?.disabled || !!bulkPilotMode || !!readOnly;
  const effectiveResolved: ResolvedFieldStateV2 | undefined = resolved
    ? { ...resolved, hidden: !!effectiveHidden, disabled: effectiveDisabled }
    : (effectiveDisabled ? { hidden: false, disabled: true, required: false } : undefined);

  // On first mount, if the field has a configured defaultValue and no value
  // has been recorded yet, commit the default through the full adapter write
  // path — uses the inspector-selected value. This ensures
  // pass/fail state, rule evaluation, and violation captures all process
  // the default on first render. Guard: existing value → skip (re-inspection
  // continuity). date/dateTime handle their own defaults in their useEffect.
  // Completed/readOnly inspections must NEVER commit defaults.
  useEffect(() => {
    if (readOnly) return;
    // Resolve the effective default (field.defaultValue OR extras.defaultState /
    // defaultToToday / defaultToNow per template-builder UI conventions).
    const dv = resolveFieldDefault(field);
    if (dv == null) return;
    if (value != null || isNA) return;
    if (NON_DEFAULT_FIELD_TYPES.has(field.type)) return;
    // Defer the commit by one microtask. Child effects fire BEFORE parent
    // effects in React, so without this defer the onFieldChange would write
    // through the adapter BEFORE InspectionRenderer’s subscription useEffect
    // has fired — notify() would have no listeners and the parent would
    // never re-render to pick up the seeded default. The microtask runs
    // after all React effects in the current commit phase, guaranteeing
    // the subscriber is in place before we notify.
    queueMicrotask(() => onFieldChange(field.id, dv));
  }, [field.id, resetKey, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // Engine returns hidden=true; we already null out inside FieldWrapper but
  // some widgets (instructions, section, dataBound) don't use the wrapper.
  // Doing the check here keeps the contract uniform.
  if (effectiveResolved?.hidden) return null;

  // Count logged occurrences across ALL mappings for this field. The
  // guard fires when this is > 0 AND the proposed value would flip the
  // field out of its failing state.
  //
  // P7 follow-up — exclude `retracted` occurrences. A retracted occurrence is
  // a removed audit tombstone (soft-delete kept so the completion rollup does
  // not resurrect it); it is already hidden from the inspector's working list
  // and counts via `visibleOccurrences` in ViolationCaptureBlock. The toggle
  // guard must agree: once every occurrence on a field is retracted, the field
  // has zero live occurrences and the inspector can flip it back to passing.
  // `repaired` occurrences still count — the field stays failed for audit.
  function countLoggedOccurrences(): number {
    if (!violationsCtx) return 0;
    let total = 0;
    for (const v of violationsCtx.violations) {
      if (v.fieldId !== field.id) continue;
      total += (v.occurrences ?? []).filter((o) => o.correctionStatus !== 'retracted').length;
    }
    return total;
  }

  const onChange = (next: unknown) => {
    if (readOnly) return;
    // Only fail-capable widgets carry the guard — other field types have
    // no fail → pass transition.
    if (isPassFailField(field)) {
      const wasFailing = isPassFailFailing(field, value);
      const willBeFailing = isPassFailFailing(field, next);
      if (wasFailing && !willBeFailing) {
        const occurrenceCount = countLoggedOccurrences();
        if (occurrenceCount > 0) {
          setBlockedChange({ occurrenceCount, kind: 'passFlip' });
          return; // BLOCK: do not propagate the change to the adapter.
        }
      }
    }
    onFieldChange(field.id, next);
  };

  // G-101 — N/A occurrence guard. Toggling a field to N/A when it has
  // logged violation occurrences is equivalent to flipping it to passing:
  // N/A suppresses violation creation, so existing occurrences would
  // become orphaned historical records with no parent field state to
  // justify them. Block the toggle and show the same modal.
  const onNA = (next: boolean) => {
    if (readOnly) return;
    if (next) {
      const occurrenceCount = countLoggedOccurrences();
      if (occurrenceCount > 0) {
        setBlockedChange({ occurrenceCount, kind: 'naToggle' });
        return;
      }
    }
    onToggleNA?.(field.id, next);
  };

  const extrasObj = (field.extras ?? {}) as Record<string, unknown>;
  const hasLegacyNaBehavior = Array.isArray(extrasObj.conditionalBehaviors)
    && (extrasObj.conditionalBehaviors as Array<{ when?: { value?: unknown } }>).some(
      (b) => String(b?.when?.value ?? '').trim().toUpperCase() === 'NA',
    );
  const effectiveNaEnabled = Boolean(
    field.naEnabled
    || isNA
    || extrasObj.showNotApplicable === true
    || extrasObj.isNAByDefault === true
    || hasLegacyNaBehavior,
  );
  const effectiveField = effectiveNaEnabled !== Boolean(field.naEnabled)
    ? { ...field, naEnabled: effectiveNaEnabled }
    : field;

  // Compute required-unmet centrally so every widget's wrapper, the section
  // header badge, and the form-tab badge agree.
  const requiredUnmet =
    isRequiredEffective(field, effectiveResolved) &&
    !isFieldAnswered(field, resolveFieldValueForValidation(field, value, isNA, resolveContext ?? undefined), isNA);
  const loggedOccurrenceCount = countLoggedOccurrences();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const baseProps: any = {
    field: effectiveField,
    value,
    isNA,
    resolved: effectiveResolved,
    onChange,
    onToggleNA: onNA,
    bulkPilotMode,
    readOnly,
    errorMessage,
    requiredUnmet: !readOnly && requiredUnmet,
    requiredImmediate: props.requiredImmediate,
    occurrenceCount: loggedOccurrenceCount,
  };

  // Single place that decides what the widget itself looks like. The
  // inline violation-capture surface is appended below the widget by the
  // wrapping fragment so EVERY widget with violation mappings gets the
  // capture UI without per-widget code (location field, notes, photo
  // attach, occurrences list, multi-occurrence support).
  function renderWidget(): ReactNode {
    switch (field.type) {
      case 'toggle':         return <ToggleField {...baseProps} />;
      case 'text':           return <TextField {...baseProps} />;
      case 'textarea':       return <TextareaField {...baseProps} />;
      case 'number':         return <NumberField {...baseProps} />;
      case 'measurement':    return <MeasurementField {...baseProps} />;
      case 'dropdown':       return <DropdownField {...baseProps} />;
      case 'checkboxGroup':  return <CheckboxGroupField {...baseProps} />;
      case 'radioGroup':     return <RadioGroupField {...baseProps} />;
      case 'camera':         return <CameraField {...baseProps} />;
      case 'date':           return <DateField {...baseProps} />;
      case 'dateTime':       return <DateTimeField {...baseProps} />;
      case 'photo':          return <PhotoField {...baseProps} />;
      case 'signature':      return <SignatureField {...baseProps} />;
      case 'location':       return <LocationField {...baseProps} />;
      case 'section':        return <SectionHeadingField field={field} resolved={effectiveResolved} />;
      case 'instructions':   return <InstructionsField field={field} value={!!value} resolved={effectiveResolved} onChange={onChange as (next: boolean) => void} />;
      case 'repeatableList': return (
        <RepeatableListField
          {...baseProps}
          renderNested={(nested, nestedValue, nestedOnChange) => (
            <FieldRenderer
              key={nested.id}
              field={nested}
              value={nestedValue}
              formId={formId}
              onFieldChange={(_id, next) => nestedOnChange(next)}
              bulkPilotMode={bulkPilotMode}
              readOnly={readOnly}
            />
          )}
        />
      );
      case 'dataBound':      return (
        <DataBoundField
          field={field}
          value={value as string | number | null | undefined}
          resolved={effectiveResolved}
          bulkPilotMode={bulkPilotMode}
          readOnly={readOnly || props.dataBoundReadOnly}
          onChange={(next) => onChange(next)}
          requiredUnmet={!readOnly && requiredUnmet}
        />
      );
      case 'inspectors':     return <InspectorField {...baseProps} />;
      default:               return <UnknownField field={field} />;
    }
  }

  // CRITICAL: widget + violation captures must live in ONE container so the
  // surrounding section grid treats them as a single grid cell. A React
  // Fragment would let both children become direct grid items — the violation
  // panel would land in the next column to the right of the failing field
  // instead of stacked underneath it.
  return (
    <div data-field-cell={field.id} className="flex flex-col gap-2 min-w-0">
      {renderWidget()}
      <FieldViolationCaptures
        field={field}
        value={value}
        isNA={isNA}
        resolved={effectiveResolved}
        formId={formId ?? ''}
        bulkPilotMode={bulkPilotMode}
        readOnly={readOnly || bulkPilotMode}
      />
      {blockedChange && (
        <OccurrenceGuardModal
          occurrenceCount={blockedChange.occurrenceCount}
          fieldLabel={field.label}
          kind={blockedChange.kind}
          onClose={() => setBlockedChange(null)}
        />
      )}
    </div>
  );
}

/**
 * Modal shown when an inspector attempts to flip a fail-capable widget
 * back to a passing value (or toggle N/A) while occurrences are still
 * logged on it. Single OK button — the only path forward is to dismiss
 * this modal and remove the occurrences (or repair them via the green
 * Repair button) one at a time using the controls in the violation panel.
 *
 * G-101: the `kind` prop distinguishes between pass-flip and N/A-toggle
 * blocked states so the title and messaging are context-specific.
 *
 * @component OccurrenceGuardModal
 */
function OccurrenceGuardModal({
  occurrenceCount,
  fieldLabel,
  kind,
  onClose,
}: {
  occurrenceCount: number;
  fieldLabel: string;
  kind: 'passFlip' | 'naToggle';
  onClose: () => void;
}): ReactNode {
  const plural = occurrenceCount === 1 ? 'occurrence' : 'occurrences';
  const title = kind === 'naToggle'
    ? 'Cannot mark this field as N/A'
    : 'Cannot change this answer to passing';
  const explanation = kind === 'naToggle'
    ? 'Marking this field as N/A would orphan the existing violation records. Remove or repair every occurrence before marking the field as not applicable.'
    : 'A violation that has been observed is a historical record — it cannot be erased by flipping this field back to passing.';
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pf-blocked-title"
      data-modal={kind === 'naToggle' ? 'na-toggle-blocked' : 'pass-fail-change-blocked'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl border border-red-200">
        <div className="px-4 py-3 border-b border-red-200 bg-red-50 rounded-t-lg">
          <h2 id="pf-blocked-title" className="text-sm font-semibold text-red-800">
            {title}
          </h2>
        </div>
        <div className="px-4 py-3 text-sm text-slate-700 space-y-2">
          <p>
            <span className="font-medium">{fieldLabel}</span> has{' '}
            <span className="font-semibold text-red-700">{occurrenceCount} logged {plural}</span>.
            {' '}{explanation}
          </p>
          <p>
            To proceed, choose one of the following actions in the violation panel
            below this field:
          </p>
          <ul className="list-disc list-inside space-y-1 text-xs text-slate-700">
            <li>
              <span className="font-medium">Repair</span> each occurrence (green
              button next to it) if the issue has been corrected. The violation
              record stays on file; this field will continue to display as failed
              for audit purposes.
            </li>
            <li>
              <span className="font-medium">Remove</span> each occurrence one at a
              time if it was logged in error. Once every occurrence is removed you
              can {kind === 'naToggle' ? 'mark this field as N/A' : 'change this answer'}.
            </li>
          </ul>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end">
          <button
            type="button"
            data-action="pf-blocked-ok"
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-semibold text-white bg-red-600 rounded-sm hover:bg-red-700"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
